import { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useRef, type CSSProperties } from 'react';
import { getWorkPolicy } from '../../api/hr-schedule';
import { HHMM } from './all-day-work-hours';
import type { CalendarEventDto } from '../../api/calendar';
import { TimeGridView } from './TimeGridView';
import { useContinuousDays } from './useContinuousDays';
import { HOUR_PX, WEEK_INITIAL_TOP_HOUR, topForHhmm } from './timeGridMetrics';
import './TimeGridStream.css';

/**
 * 주간·일간 뷰의 «끊기지 않는 가로 연속 스크롤» 껍데기 (C-2a).
 *
 * ## ★ 이 컴포넌트가 «마크업을 안 베낀다»
 *
 * 정본(`feature/dev-kyeongeun`)은 `TimeGridView` 를 **지우고** `TimeGridStream` 으로 바꿨는데,
 * 그 정본 마크업은 **그때의** main 에 없는 것 **넷**을 썼다 — `useCalendarCreateDnD`(슬롯 끌어
 * 만들기) · `ReservationBadge` · `minutesLink` · `timeGridMetrics`.
 *
 * ⭐⭐ **정정 (2026-10-02 · 다시 쟀다): 그 넷 중 ★ «셋이 들어왔고 화면에도 붙었다».**
 * 종전 줄은 「둘만 들어왔고 아직 화면에 안 붙였다 · 남은 것 둘」이었는데 **셋 다 낡았다.**
 * ```
 * timeGridMetrics      ✅ 들어오고 «붙었다» — HOUR_PX 선언이 캘린더에 «하나»뿐이다(60)
 *                         (`timeGridMetrics.ts:8`. 종전 줄의 「그리는 48」은 없다.
 *                          `ReservationPage/reservation-week.ts:17` 의 44 는 «예약 화면» 것이고 별개다)
 * useCalendarCreateDnD ✅ 들어오고 «붙었다» — `TimeGridView.tsx:7` 이 import 한다
 *                         (끝에서 끝까지 재는 시험: `slot-create-wire.test.tsx`)
 * ReservationBadge     ✅ 들어오고 «붙었다» — 제품 파일 ★ 7곳이 import 한다
 *                         (CalendarDayRail · CalendarUpcoming · DayAgendaView · EventDetailModal ·
 *                          MonthView · TimeGridView · eventTooltip)
 * minutesLink          ⛔ ★ 아직 «없다» — 이것 «하나»가 남은 것이다
 * ```
 * ⇒ 그 수를 만든 명령(origin/main · `git grep`):
 * ```
 *   git grep -o 'ReservationBadge' origin/main -- apps/frontend/src | wc -l       ⇒ 43 (파일 14)
 *   git grep -l 'import .*ReservationBadge' origin/main -- apps/frontend/src      ⇒ 10 (제품 7 · 시험 2 · 제 CSS 1)
 *   git grep -o 'minutesLink' origin/main -- apps/frontend/src | wc -l            ⇒ 2
 * ```
 *
 * ## ⛔⛔ 그 「2」를 ★ «남았다»로 읽지 말 것 — **주석이 자기를 센다**
 *
 * `minutesLink` 가 2건 나오는데 **그 둘이 바로 이 주석 자신**이다(제품 코드 0 · 시험 0).
 * ⇒ ★ 그러니 「아직 없나」를 **그 이름으로 세면 영원히 0 이 안 된다.**
 *   ⇒ ⭐ 재는 법은 **주석을 길이 보존으로 덮고 세는 것**이고, 그것을
 *     `timegridstream-remaining.guard.test.ts` 가 한다 — **들어오는 날 그 가드가 빨개진다.**
 * ⚠ 그리고 **언제 들어올지는 이 파일이 모른다 — 회의록 만들기가 «중지»돼 있다.**
 * ```
 *   2026-09-30 사용자 지시로 중지 ⇒ ★ 근거와 ⭐ «범위»는 한 파일에 있다:
 *     docs/dev/voice-minutes-finalize-halted.md   ← 머리말에 지시 원문이 있다
 * ```
 * ⚠ **그 범위를 여기 베껴 적지 않는다** — 중지가 «어디까지»인지는 아직 정해지는 중이고,
 *   베껴 두면 그 사본이 낡아 이 머리말이 또 거짓이 된다(이 파일이 방금 겪은 그것이다).
 *   ⇒ ★ 그래서 **가리키기만** 한다.
 * ⚠ 그리고 그 지시는 **웹 소스에 없다** — `apps/frontend` 를 훑어서는 0건이다(처음에 그렇게
 *   재고 「근거를 못 찾았다」고 적었다). ⇒ ★ **「0건」은 「없다」가 아니라 「그 자리에는 없다」**다.
 * ⇒ ⇒ ★ 그러므로 **「곧 들어온다」고 읽지 말 것** — 일정은 사용자가 정한다.
 *
 * 그래서 사용자 방침(「디자인과 기능을 그대로, **붙는 자리만 지금 main 에**」)대로 **동작만**
 * 옮겼다 — 날짜 스트림은 `useContinuousDays`(정본 그대로, 한 줄도 안 고쳤다)가 만들고,
 * **그리는 일은 main 의 `TimeGridView` 에 그대로 맡긴다.** 겹침 배치·드래그·작성자 아바타는
 * 그 컴포넌트가 이미 하고 있으므로 **두 벌로 만들지 않는다.**
 *
 * ```
 * <div .calendar-timegrid-stream>   ← 가로로 스크롤되는 것은 «이 껍데기»다
 *   <TimeGridView days={...} />     ← main 것 그대로. 한 줄도 안 고쳤다
 * </div>
 * ```
 *
 * ## ⛔ 종전 줄: 「지금은 «아무 데서도 안 쓴다»」 — ★ **그것도 낡았다 (2026-10-02 재서 확인)**
 *
 * 처음 판은 둘로 가른 것 중 앞쪽이어서 **쓰는 곳이 0** 이었고, 「갈아타는 것은 다음 판(C-2b)」
 * 이라 적었다. **그 C-2b 는 끝났다.**
 * ```
 *   git grep -l 'TimeGridStream' origin/main -- apps/frontend/src  ⇒ 12 파일
 *   그중 제품: CalendarPage.tsx · useCoalescedRange.ts · 이 파일 · 제 CSS
 *   ⇒ ★ CalendarPage 가 «이 컴포넌트로 그린다» — 화면이 이것을 탄다
 * ```
 * ⇒ ⚠ 그래서 **이 파일을 고치면 주간·일간 화면이 바뀐다.** 종전 줄을 믿고
 *   「안 쓰니 안전하다」로 읽으면 안 된다.
 *
 * ## ⚠ 서버는 손대지 않는다 (재서 확인)
 *
 * 정본 `useCoalescedRange` 가 **`MAX_SPAN_DAYS = 210`** 으로 스스로 묶고, 그 머리말이
 * 「서버 상한(365일) 아래이기도 하다」를 적고 있다. main 의 상한은
 * `calendar-helpers.ts:24` **`MAX_RANGE_MS = 365일`** — 210 < 365 로 안 넘는다.
 * 질의도 `listQuerySchema` 의 `from`·`to`·`calendarIds` 로 그대로 된다.
 *
 * ⚠ `listEvents` 의 **`signal` 은 이미 있다**(`api/calendar.ts:137` · 2026-09-28 검색 판이
 *   additive 로 넣었다). 호출부 **7곳** 중 **1곳(`EventSearch`)**이 쓴다 — **더할 것이 없다.**
 *   (다음 사람이 「signal 이 없다」로 또 읽지 않도록 수를 적어 둔다.)
 *
 * ## ⛔ 눈으로 안 본 것
 *
 * **화면을 띄워 보지 않았다** — 그 판의 세션에는 「로컬 서버를 띄우지 마세요」가 서 있었다. 그래서
 * `TimeGridStream.css` 의 치수·고정(sticky) 은 **미검증**이었고, 「C-2b 에서 전/후 캡처와 함께
 * 확인한다」고 적어 두었다.
 * ⚠ **2026-10-02 현재: 그 확인이 실제로 있었는지 «나는 못 쟀다».** C-2b 자체는 끝났다(위 참조)
 *   지만, 캡처가 남았는지는 이 레포에서 확인하지 않았다. ⇒ ★ 그러므로 이 줄을 **「확인됐다」로
 *   읽지 말 것** — 지우지 않고 남겨 둔다(지우면 「본 적 있다」는 뜻이 된다).
 */

export interface TimeGridStreamHandle {
  /** 임의 날짜로 점프 — 창 안이면 스크롤만, 창 밖이면 창을 다시 만든다. */
  scrollToDate: (date: Date) => void;
}

export interface TimeGridStreamProps {
  /** 처음 화면에 걸릴 날짜. 창은 이 날짜를 가운데 두고 앞뒤로 만들어진다. */
  initialDate: Date;
  /** 7=주간(밀도) · 1=일간(밀도). 날짜 스트림은 공유하고 칸 폭만 다르다. */
  perView: 1 | 7;
  events: CalendarEventDto[];
  loading: boolean;
  onEventClick: (e: CalendarEventDto) => void;
  onEventMove?: (e: CalendarEventDto, deltaMinutes: number) => void;
  /** 빈 칸을 끌어 만들기 — 그리는 일은 `TimeGridView` 가 한다. 안 넘기면 그 기능이 꺼진다. */
  onSlotCreate?: (start: Date, end: Date) => void;
  /**
   * 마운트된 날짜 범위가 바뀔 때마다(확장·점프) 그 구간을 알린다.
   * ⛔ **부르는 쪽은 이것을 `useCoalescedRange` 로 감싸야 한다** — 안 감싸면 가장자리를 붙잡고
   *   있을 때 초당 십수 건이 나간다(정본 실측: 5초에 74건 · 다른 화면까지 429).
   */
  onNeedRange: (from: Date, to: Date) => void;
  /** 화면 왼쪽에 걸린 날짜가 바뀔 때 — 헤더 제목 동기화용. */
  onVisibleDayChange?: (date: Date) => void;
  /** 하루종일 줄 높이를 맞출 주(헤더의 주) — TimeGridView 로 그대로 넘긴다. */
  allDayFocus?: { start: Date; end: Date } | null;
  /** 날짜 머리 누르기 → 그 날짜 고르기(우측 레일). 그대로 `TimeGridView` 에 넘긴다(옵셔널 · 2026-10-13). */
  onDayHeadClick?: (date: Date) => void;
  /** 우측 레일이 보고 있는 날짜 — 그 머리를 강조(옵셔널). */
  selectedDate?: Date | null;
}

export const TimeGridStream = forwardRef<TimeGridStreamHandle, TimeGridStreamProps>(
  function TimeGridStream(
    { initialDate, perView, events, loading, onEventClick, onEventMove, onSlotCreate, onNeedRange, onVisibleDayChange, onDayHeadClick, selectedDate, allDayFocus },
    ref,
  ) {
    const containerRef = useRef<HTMLDivElement>(null);
    const { days, colWidth, onScroll, scrollToDate } = useContinuousDays({
      containerRef,
      initialDate,
      perView,
      onNeedRange,
      onVisibleDayChange,
    });

    useImperativeHandle(ref, () => ({ scrollToDate }), [scrollToDate]);

    /**
     * 주간을 열면 맨 위가 00시가 아니라 **09시**(근무 정책을 받기 전 자리 — 받으면 아래 효과가 근무 시작으로 옮긴다, 2026-10-23)가 되게.
     *
     * ⭐ 정본은 이 줄을 «제 TimeGridStream» 안에 뒀다. 여기서도 자리가 같다 — **굴러가는 것이
     *   이 껍데기**이기 때문이다(C-2b 에서 안쪽 `.calendar-timegrid` 의 스크롤을 껐다).
     * ⚠ 마운트 때 «한 번만» — 그 뒤 사용자가 옮긴 세로 위치는 건드리지 않는다. 이전/다음·오늘은
     *   가로(`scrollToDate`)만 움직이므로 보던 시간대가 그대로 유지된다.
     */
    useLayoutEffect(() => {
      const el = containerRef.current;
      if (el) el.scrollTop = WEEK_INITIAL_TOP_HOUR * HOUR_PX;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * ⭐ 2026-10-23 사용자 지시 「웹도 근무 시작 시간으로」 — 앱(지시 18)과 같은 규칙.
     *   여는 날의 내 근무 정책(`GET /hr/attendance/policy?date=` · 근태 화면 · 하루종일 시각과 같은 것)의
     *   `baseStart` 가 맨 위에 오게 한 번만 옮긴다. 받기 전에는 위 09시 자리, 못 받으면 그대로.
     * ⚠ 사용자가 먼저 직접 굴렸으면 늦게 온 응답으로 끌어오지 않는다(사용자 스크롤 우선).
     * ⚠ 마운트 때 «한 번만» — 이전/다음·오늘은 가로만 움직이고 보던 시간대는 그대로(위 주석과 같은 원칙).
     */
    useEffect(() => {
      const el = containerRef.current;
      if (!el) return;
      let alive = true;
      let touched = false;
      const mark = () => { touched = true; };
      const kinds = ['wheel', 'touchstart', 'keydown', 'mousedown'] as const;
      kinds.forEach((k) => el.addEventListener(k, mark, { passive: true }));
      const d = initialDate;
      const date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      getWorkPolicy(date)
        .then((p) => {
          if (!alive || touched || !HHMM.test(p?.baseStart ?? '')) return;
          const top = topForHhmm(p.baseStart);
          if (el.scrollTop === top) return;
          if (typeof el.scrollTo === 'function') el.scrollTo({ top, behavior: 'smooth' });
          else el.scrollTop = top;
        })
        .catch(() => { /* 물러서는 자리(09시) 그대로 */ });
      return () => {
        alive = false;
        kinds.forEach((k) => el.removeEventListener(k, mark));
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * ⛔⛔ 날짜 머리가 «종일 줄»에 가리는 것을 막는다 — 캡처로 찾았다 (2026-09-29).
     *
     * `CalendarPage.css` 는 **날짜 머리와 종일 줄에 똑같이 `top: 0; z-index: 2`** 를 준다.
     * 둘 다 같은 스크롤러 안에서 위에 붙으므로, **세로로 굴리면 나중에 그려지는 종일 줄이
     * 날짜 머리를 덮는다.** main 에도 있는 일이지만 거기서는 주간이 **맨 위(00시)에서 열려**
     * 굴리기 전에는 안 보였다. 이 판이 **08시로 열면서** 그 가림이 **첫 화면부터** 드러났다
     * (캡처에서 요일·날짜 줄이 통째로 사라진 것을 보고 찾았다).
     *
     * ⇒ 고치는 자리는 **이 껍데기**다(`CalendarPage.css` 는 안 건드린다):
     *   · 머리의 실제 높이를 재서 `--tg-headh` 로 내려 주고
     *   · CSS 가 종일 줄을 **그 높이만큼 아래에** 붙인다(`TimeGridStream.css`)
     * ⚠ 높이를 글자로 박지 않는다 — 글씨 크기·테마·종일 칩 수에 따라 달라진다. 그래서 잰다.
     */
    useLayoutEffect(() => {
      const shell = containerRef.current;
      const head = shell?.querySelector<HTMLElement>('.calendar-timegrid-header');
      if (!shell || !head) return;
      const apply = () => shell.style.setProperty('--tg-headh', `${head.offsetHeight}px`);
      apply();
      if (typeof ResizeObserver !== 'function') return;
      const ro = new ResizeObserver(apply);
      ro.observe(head);
      return () => ro.disconnect();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
      <div
        ref={containerRef}
        className="calendar-timegrid-stream"
        onScroll={onScroll}
        data-testid="timegrid-stream"
        // 칸 폭은 컨테이너 폭에서 재서 나온 값이다 — CSS 가 이 변수로 열 폭을 정한다.
        style={{ ['--tg-col-width' as keyof CSSProperties]: `${colWidth}px` } as CSSProperties}
      >
        <TimeGridView
          days={days}
          events={events}
          loading={loading}
          onEventClick={onEventClick}
          onEventMove={onEventMove}
          onSlotCreate={onSlotCreate}
          onDayHeadClick={onDayHeadClick}
          selectedDate={selectedDate}
          allDayFocus={allDayFocus}
        />
      </div>
    );
  },
);
